"use client";

import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { SectionLink } from "./SectionLink";

// Left-hand menu on a category page. Highlights the section currently in view
// and scrolls smoothly to a section when clicked.
export function SectionNav({ sections }) {
  const [activeId, setActiveId] = useState(sections[0]?.id);
  // True while a click-started smooth scroll is running, so the sections it
  // passes on the way don't flash in the menu.
  const isClickScrolling = useRef(false);
  const releaseTimer = useRef(null);

  useEffect(() => {
    // The current section is the last one whose heading has scrolled past the
    // top third of the screen - or the first one before any has.
    const updateActive = () => {
      if (isClickScrolling.current) return;

      const threshold = window.innerHeight / 3;
      let current = sections[0]?.id;

      sections.forEach((section) => {
        const element = document.getElementById(section.id);
        if (element && element.getBoundingClientRect().top <= threshold) {
          current = section.id;
        }
      });

      setActiveId(current);
    };

    const releaseClickScroll = () => {
      isClickScrolling.current = false;
    };

    updateActive();
    window.addEventListener("scroll", updateActive, { passive: true });
    window.addEventListener("scrollend", releaseClickScroll);
    return () => {
      window.removeEventListener("scroll", updateActive);
      window.removeEventListener("scrollend", releaseClickScroll);
      clearTimeout(releaseTimer.current);
    };
  }, [sections]);

  const handleClick = (id) => {
    setActiveId(id);
    isClickScrolling.current = true;
    // Fallback for browsers without the scrollend event.
    clearTimeout(releaseTimer.current);
    releaseTimer.current = setTimeout(() => {
      isClickScrolling.current = false;
    }, 1000);
  };

  return (
    <nav className="rounded-2xl border border-neutral-200 bg-white p-3">
      <ul className="space-y-1">
        {sections.map((section) => (
          <li key={section.id}>
            <SectionLink
              id={section.id}
              onClick={() => handleClick(section.id)}
              className={cn(
                "flex items-start gap-2 rounded-lg px-3 py-2.5 text-neutral-700 transition-colors hover:bg-neutral-100",
                activeId === section.id &&
                  "bg-neutral-100 font-medium text-neutral-900 hover:bg-neutral-100",
              )}
            >
              {/* Every item keeps the dot's space so the labels stay aligned. */}
              <span
                className={cn(
                  "mt-[9px] size-1.5 shrink-0 rounded-full bg-neutral-900",
                  activeId !== section.id && "invisible",
                )}
              />
              {section.title}
            </SectionLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}
